import { useCurrentUser } from 'components/providers'
import dayjs from 'dayjs'
import { QueryConstraint, where } from 'firebase/firestore'
import { createContext, useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { CollectionName, IWorkspace } from 'types'
import { FetchStatus, getCount, useDoc, usePrefCurrentWorkspace } from 'utils'

export interface WorkspaceContextProps {
  countByCollection?: CountByCollection
  status: FetchStatus
  userCount?: number
  workspace: IWorkspace | null
}

type CountCollectionName = Extract<CollectionName, 'events' | 'facilities' | 'templates'>
type CountByCollection = Record<CountCollectionName, number>

export const WorkspaceContext = createContext<WorkspaceContextProps>({} as any)

export function WorkspaceProvider({ children }: { children: any }) {
  const user = useCurrentUser()
  const id = useParams().workspaceId
  const navigate = useNavigate()
  const [_, updateWorkspaceId] = usePrefCurrentWorkspace()
  const [workspace, docStatus] = useDoc('Workspace', id)
  const [countByCollection, setCountByCollection] = useState<CountByCollection>()

  const status = docStatus === 'success' && !countByCollection ? 'loading' : docStatus
  const userCount = workspace
    ? workspace.userIds.length + Object.keys(workspace.pendingUserByEmail).length
    : undefined

  useEffect(() => {
    if (docStatus === 'loading') return

    const hasAccess = user?.isAdmin || Boolean(user && workspace?.userById[user.id])
    if (!workspace || !hasAccess) {
      updateWorkspaceId('')
      return navigate('/', { replace: true })
    }

    updateWorkspaceId(id ?? '')
    setCountByCollection(undefined)

    const resetDate = workspace.subscription?.limitsResetDate
    const limitStartDate = dayjs(resetDate).subtract(1, 'year').unix() * 1000

    const makeQuery = (collection: CollectionName, constraints?: QueryConstraint[]) =>
      getCount(collection, where('workspaceId', '==', id), ...(constraints || []))

    const eventConstraints = resetDate
      ? [where('startTime', '>=', limitStartDate), where('startTime', '<=', resetDate)]
      : undefined
    const queries: Array<{ name: CountCollectionName; getCount: Promise<number> }> = [
      { name: 'events', getCount: makeQuery('events', eventConstraints) },
      { name: 'facilities', getCount: makeQuery('facilities') },
      { name: 'templates', getCount: makeQuery('templates') },
    ]

    Promise.all(queries.map(q => q.getCount)).then(counts => {
      setCountByCollection(
        counts.reduce((acc, count, index) => {
          acc[queries[index].name] = count
          return acc
        }, {} as CountByCollection)
      )
    })
  }, [workspace, docStatus])

  return (
    <WorkspaceContext.Provider
      value={{ countByCollection, userCount, workspace: workspace || null, status }}
    >
      {children}
    </WorkspaceContext.Provider>
  )
}

export const useWorkspace = () => useContext(WorkspaceContext)
